(() => {
  const money = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value) || 0);

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character]);

  const initAdmin = (root) => {
    if (!root || root.dataset.wholesaleAdminV2Ready === 'true') return;
    root.dataset.wholesaleAdminV2Ready = 'true';

    const apiBase = String(root.dataset.apiBase || 'https://calculator.viallock.com').replace(/\/$/, '');
    const signin = root.querySelector('[data-wholesale-admin-signin]');
    const loginForm = root.querySelector('[data-wholesale-admin-login]');
    const usernameInput = root.querySelector('[data-wholesale-admin-email]');
    const passwordInput = root.querySelector('[data-wholesale-admin-password]');
    const message = root.querySelector('[data-wholesale-admin-message]');
    const workspace = root.querySelector('[data-wholesale-admin-workspace]');
    const applicationsList = root.querySelector('[data-wholesale-admin-applications]');
    const applicationCount = root.querySelector('[data-wholesale-admin-application-count]');
    const createAccountForm = root.querySelector('[data-wholesale-admin-create-account]');
    const createClinicInput = root.querySelector('[data-wholesale-admin-create-clinic]');
    const createEmailInput = root.querySelector('[data-wholesale-admin-create-email]');
    const accountSelect = root.querySelector('[data-wholesale-admin-account]');
    const accountMeta = root.querySelector('[data-wholesale-admin-account-meta]');
    const activeToggle = root.querySelector('[data-wholesale-admin-active]');
    const resetPasswordButton = root.querySelector('[data-wholesale-admin-reset-password]');
    const searchInput = root.querySelector('[data-wholesale-admin-search]');
    const rows = root.querySelector('[data-wholesale-admin-rows]');
    const saveButton = root.querySelector('[data-wholesale-admin-save]');
    const resetButton = root.querySelector('[data-wholesale-admin-reset]');
    const logoutButton = root.querySelector('[data-wholesale-admin-logout]');
    const dirtyCount = root.querySelector('[data-wholesale-admin-dirty]');
    const bulkMode = root.querySelector('[data-wholesale-admin-bulk-mode]');
    const bulkValue = root.querySelector('[data-wholesale-admin-bulk-value]');
    const rounding = root.querySelector('[data-wholesale-admin-rounding]');
    const applyVisibleButton = root.querySelector('[data-wholesale-admin-apply-visible]');
    const applyAllButton = root.querySelector('[data-wholesale-admin-apply-all]');
    const copyAccountSelect = root.querySelector('[data-wholesale-admin-copy-account]');
    const copyPricingButton = root.querySelector('[data-wholesale-admin-copy-pricing]');
    const exportButton = root.querySelector('[data-wholesale-admin-export]');
    const importInput = root.querySelector('[data-wholesale-admin-import]');

    let accounts = [];
    let applications = [];
    let catalog = [];
    let pricing = {};
    let editedPricing = {};

    const token = () => sessionStorage.getItem('vl_wholesale_admin_token') || '';
    const setToken = (value) => {
      localStorage.removeItem('wh_token');
      if (value) sessionStorage.setItem('vl_wholesale_admin_token', value);
      else sessionStorage.removeItem('vl_wholesale_admin_token');
    };

    const showMessage = (text, tone = 'info') => {
      if (!message) return;
      message.hidden = !text;
      message.dataset.tone = tone;
      message.textContent = text || '';
    };

    const api = async (path, options = {}) => {
      const response = await fetch(`${apiBase}${path}`, {
        ...options,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token()}`,
          ...(options.headers || {}),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || 'Unable to complete the wholesale admin request.');
        error.status = response.status;
        throw error;
      }
      return payload;
    };

    const activeAccount = () => accounts.find(
      (account) => String(account.id) === String(accountSelect?.value),
    ) || accounts[0] || null;

    const flattenCatalog = () => catalog.flatMap((product) => (product.variants || []).map((variant) => ({
      ...variant,
      productId: product.id,
      productTitle: product.title,
      productHandle: product.handle,
    })));

    const visibleRows = () => {
      const query = String(searchInput?.value || '').trim().toLowerCase();
      return flattenCatalog().filter((variant) => [
        variant.productTitle,
        variant.title,
        variant.color,
        variant.sku,
      ].join(' ').toLowerCase().includes(query));
    };

    const currentPrice = (variant) => {
      if (Object.prototype.hasOwnProperty.call(editedPricing, variant.id)) return editedPricing[variant.id];
      if (Object.prototype.hasOwnProperty.call(pricing, variant.id)) return pricing[variant.id];
      return '';
    };

    const effectivePrice = (variant) => {
      const custom = currentPrice(variant);
      return custom === '' ? Number(variant.defaultWholesalePrice ?? variant.price) : Number(custom);
    };

    const renderDirty = () => {
      const changes = Object.keys(editedPricing).length;
      if (dirtyCount) dirtyCount.textContent = changes
        ? `${changes} unsaved price ${changes === 1 ? 'change' : 'changes'}`
        : 'No unsaved changes';
      if (saveButton) saveButton.disabled = !activeAccount() || !changes;
      if (resetButton) resetButton.disabled = !changes;
      if (applyVisibleButton) applyVisibleButton.disabled = !activeAccount();
      if (applyAllButton) applyAllButton.disabled = !activeAccount();
      if (exportButton) exportButton.disabled = !activeAccount() || !catalog.length;
    };

    const renderRows = () => {
      if (!rows) return;
      const variants = visibleRows();
      rows.innerHTML = variants.length ? variants.map((variant) => {
        const custom = currentPrice(variant);
        const isEdited = Object.prototype.hasOwnProperty.call(editedPricing, variant.id);
        const hasSaved = Object.prototype.hasOwnProperty.call(pricing, variant.id);
        const status = isEdited ? 'Edited' : hasSaved ? 'Custom' : 'Default';
        const tone = isEdited ? 'edited' : hasSaved ? 'custom' : 'default';
        const defaultPrice = Number(variant.defaultWholesalePrice ?? variant.price) || 0;
        const difference = effectivePrice(variant) - defaultPrice;
        return `
          <tr>
            <td>
              <strong>${escapeHtml(variant.productTitle)}</strong>
              <span>${escapeHtml(variant.sku || '')}</span>
            </td>
            <td>${escapeHtml(variant.title || variant.color || 'Standard')}</td>
            <td>${money(variant.retailPrice)}</td>
            <td>${money(defaultPrice)}</td>
            <td>
              <label>
                <span>Custom price</span>
                <input type="number" min="0" step="0.01" value="${custom === '' ? '' : escapeHtml(custom)}" placeholder="${money(defaultPrice)}" data-wholesale-admin-price data-variant-id="${escapeHtml(variant.id)}">
              </label>
            </td>
            <td>${difference ? `${money(Math.abs(difference))} ${difference < 0 ? 'lower' : 'higher'}` : '$0.00'}</td>
            <td><span class="vl-wholesale-admin__status vl-wholesale-admin__status--${tone}">${status}</span></td>
          </tr>
        `;
      }).join('') : '<tr><td colspan="7">No products match that search.</td></tr>';
      renderDirty();
    };

    const renderAccountSelectors = () => {
      const previous = accountSelect?.value;
      if (accountSelect) {
        accountSelect.innerHTML = accounts.map((account) => `
          <option value="${escapeHtml(account.id)}">${escapeHtml(account.clinicName || account.email)}</option>
        `).join('');
        if (accounts.some((account) => String(account.id) === String(previous))) accountSelect.value = previous;
      }
      if (copyAccountSelect) {
        copyAccountSelect.innerHTML = accounts
          .filter((account) => String(account.id) !== String(accountSelect?.value))
          .map((account) => `<option value="${escapeHtml(account.id)}">${escapeHtml(account.clinicName || account.email)}</option>`)
          .join('') || '<option value="">No other accounts</option>';
      }
    };

    const renderAccountMeta = () => {
      const account = activeAccount();
      if (accountMeta) {
        accountMeta.textContent = account
          ? `${account.email} • ${account.pricingTier || 'Wholesale pricing'}`
          : 'No wholesale accounts yet';
      }
      if (activeToggle) {
        activeToggle.disabled = !account;
        activeToggle.checked = account?.active !== false;
      }
      if (resetPasswordButton) resetPasswordButton.disabled = !account;
    };

    const loadPricing = async () => {
      const account = activeAccount();
      pricing = {};
      editedPricing = {};
      renderAccountMeta();
      renderAccountSelectors();
      if (!account) {
        catalog = [];
        renderRows();
        return;
      }
      showMessage(`Loading current products and pricing for ${account.clinicName || account.email}...`);
      const payload = await api(`/wholesale/admin/account-pricing?accountId=${encodeURIComponent(account.id)}`);
      catalog = Array.isArray(payload.catalog) ? payload.catalog : [];
      pricing = payload.overrides || {};
      renderRows();
      showMessage(`Loaded ${catalog.length} live Shopify products for ${account.clinicName || account.email}.`, 'success');
    };

    const renderApplications = () => {
      if (!applicationsList || !applicationCount) return;
      const pending = applications.filter((application) => application.status === 'pending');
      applicationCount.textContent = `${pending.length} pending`;
      applicationsList.innerHTML = pending.length ? pending.map((application) => `
        <article class="vl-wholesale-admin__application" data-application-id="${escapeHtml(application.id)}">
          <div>
            <strong>${escapeHtml(application.businessName)}</strong>
            <span>${escapeHtml(application.contactName)} · ${escapeHtml(application.email)}</span>
            <span>${escapeHtml([application.businessType, application.expectedMonthlyVolume, application.vialSizeInterest].filter(Boolean).join(' · '))}</span>
            ${application.notes ? `<p>${escapeHtml(application.notes)}</p>` : ''}
          </div>
          <div>
            <button type="button" data-wholesale-application-decline>Decline</button>
            <button type="button" data-wholesale-application-approve>Approve &amp; email login</button>
          </div>
        </article>
      `).join('') : '<p class="vl-wholesale-admin__empty">No pending wholesale applications.</p>';
    };

    const loadWorkspace = async () => {
      if (signin) signin.hidden = true;
      if (workspace) workspace.hidden = false;
      showMessage('Loading wholesale accounts and applications...');
      const [accountPayload, applicationPayload] = await Promise.all([
        api('/wholesale/admin/accounts'),
        api('/wholesale/admin/applications'),
      ]);
      accounts = accountPayload.accounts || [];
      applications = applicationPayload.applications || [];
      renderAccountSelectors();
      renderApplications();
      await loadPricing();
    };

    const rounded = (value) => {
      const price = Math.max(0, Number(value) || 0);
      switch (rounding?.value) {
        case '99': return Math.max(0, Math.floor(price) + 0.99);
        case '95': return Math.max(0, Math.floor(price) + 0.95);
        case 'whole': return Math.round(price);
        default: return Math.round(price * 100) / 100;
      }
    };

    const bulkPrice = (variant) => {
      const value = Number(bulkValue?.value) || 0;
      const defaultWholesale = Number(variant.defaultWholesalePrice ?? variant.price) || 0;
      switch (bulkMode?.value) {
        case 'percent_off_retail': return rounded(Number(variant.retailPrice) * (1 - value / 100));
        case 'percent_off_wholesale': return rounded(defaultWholesale * (1 - value / 100));
        case 'set_fixed': return rounded(value);
        case 'adjust_amount': return rounded(effectivePrice(variant) + value);
        default: return rounded(effectivePrice(variant));
      }
    };

    const applyBulk = (scope) => {
      if (String(bulkValue?.value || '').trim() === '') {
        showMessage('Enter a bulk pricing value first.', 'error');
        return;
      }
      const variants = scope === 'visible' ? visibleRows() : flattenCatalog();
      variants.forEach((variant) => {
        editedPricing[variant.id] = bulkPrice(variant);
      });
      renderRows();
      showMessage(`Applied the pricing rule to ${variants.length} product options. Review and save to commit it.`, 'success');
    };

    const parseCsv = (text) => {
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (!lines.length) return [];
      const parse = (line) => {
        const cells = [];
        let value = '';
        let quoted = false;
        for (let index = 0; index < line.length; index += 1) {
          const character = line[index];
          if (character === '"' && line[index + 1] === '"' && quoted) {
            value += '"';
            index += 1;
          } else if (character === '"') quoted = !quoted;
          else if (character === ',' && !quoted) {
            cells.push(value);
            value = '';
          } else value += character;
        }
        cells.push(value);
        return cells;
      };
      const headers = parse(lines[0]).map((header) => header.trim().toLowerCase());
      return lines.slice(1).map((line) => {
        const cells = parse(line);
        return Object.fromEntries(headers.map((header, index) => [header, cells[index] || '']));
      });
    };

    loginForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      showMessage('Signing in...');
      try {
        const response = await fetch(`${apiBase}/wholesale-admin/login`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: usernameInput.value.trim(),
            password: passwordInput.value,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Invalid admin credentials.');
        setToken(payload.token);
        passwordInput.value = '';
        await loadWorkspace();
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    applicationsList?.addEventListener('click', async (event) => {
      const card = event.target.closest('[data-application-id]');
      if (!card) return;
      const id = card.dataset.applicationId;
      const approve = event.target.closest('[data-wholesale-application-approve]');
      const decline = event.target.closest('[data-wholesale-application-decline]');
      if (!approve && !decline) return;
      const action = approve ? 'approve' : 'decline';
      const prompt = approve
        ? 'Approve this clinic and email a temporary password?'
        : 'Decline this application?';
      if (!window.confirm(prompt)) return;
      showMessage(approve ? 'Approving account and sending the welcome email...' : 'Declining application...');
      try {
        await api(`/wholesale/admin/applications/${encodeURIComponent(id)}/${action}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        await loadWorkspace();
        showMessage(approve ? 'Clinic approved and the temporary-password email was sent.' : 'Application declined.', 'success');
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    createAccountForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      showMessage('Creating the clinic account and sending a temporary password...');
      try {
        await api('/wholesale/admin/accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clinicName: createClinicInput.value.trim(),
            email: createEmailInput.value.trim(),
            active: true,
          }),
        });
        createAccountForm.reset();
        await loadWorkspace();
        showMessage('Clinic account created and the login email was sent.', 'success');
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    accountSelect?.addEventListener('change', () => loadPricing().catch((error) => showMessage(error.message, 'error')));
    searchInput?.addEventListener('input', renderRows);

    activeToggle?.addEventListener('change', async () => {
      const account = activeAccount();
      if (!account) return;
      try {
        const payload = await api(`/wholesale/admin/accounts/${encodeURIComponent(account.id)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: activeToggle.checked }),
        });
        Object.assign(account, payload.account || {});
        renderAccountMeta();
        showMessage(`Account ${activeToggle.checked ? 'activated' : 'paused'}.`, 'success');
      } catch (error) {
        activeToggle.checked = account.active !== false;
        showMessage(error.message, 'error');
      }
    });

    resetPasswordButton?.addEventListener('click', async () => {
      const account = activeAccount();
      if (!account || !window.confirm(`Email a new temporary password to ${account.email}?`)) return;
      try {
        await api(`/wholesale-admin/accounts/${encodeURIComponent(account.id)}/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        showMessage('Temporary password sent. The clinic must replace it at next login.', 'success');
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    rows?.addEventListener('input', (event) => {
      const input = event.target.closest('[data-wholesale-admin-price]');
      if (!input) return;
      const id = input.dataset.variantId;
      if (input.value === '') editedPricing[id] = null;
      else editedPricing[id] = Number(input.value);
      renderDirty();
    });

    saveButton?.addEventListener('click', async () => {
      const account = activeAccount();
      if (!account) return;
      showMessage(`Saving ${Object.keys(editedPricing).length} price changes for ${account.clinicName || account.email}...`);
      try {
        await api('/wholesale/admin/account-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId: account.id,
            prices: Object.entries(editedPricing).map(([variantId, price]) => ({ variantId, price })),
          }),
        });
        Object.entries(editedPricing).forEach(([id, price]) => {
          if (price === null || !Number.isFinite(Number(price)) || Number(price) <= 0) delete pricing[id];
          else pricing[id] = Number(price);
        });
        editedPricing = {};
        renderRows();
        showMessage(`Saved account-specific pricing for ${account.clinicName || account.email}.`, 'success');
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    resetButton?.addEventListener('click', () => {
      editedPricing = {};
      renderRows();
      showMessage('Unsaved pricing changes cleared.');
    });

    applyVisibleButton?.addEventListener('click', () => applyBulk('visible'));
    applyAllButton?.addEventListener('click', () => applyBulk('all'));

    copyPricingButton?.addEventListener('click', async () => {
      const sourceId = copyAccountSelect?.value;
      const target = activeAccount();
      if (!sourceId || !target) return;
      try {
        const payload = await api(`/wholesale/admin/account-pricing?accountId=${encodeURIComponent(sourceId)}`);
        const source = payload.overrides || {};
        editedPricing = { ...editedPricing, ...source };
        renderRows();
        showMessage('Copied matching variant prices. Review and save to apply them.', 'success');
      } catch (error) {
        showMessage(error.message, 'error');
      }
    });

    exportButton?.addEventListener('click', () => {
      const account = activeAccount();
      if (!account) return;
      const header = ['Product', 'Variant', 'Variant ID', 'SKU', 'Retail', 'Default wholesale', 'Account price'];
      const csv = [
        header,
        ...visibleRows().map((variant) => [
          variant.productTitle,
          variant.title,
          variant.id,
          variant.sku,
          Number(variant.retailPrice).toFixed(2),
          Number(variant.defaultWholesalePrice ?? variant.price).toFixed(2),
          effectivePrice(variant).toFixed(2),
        ]),
      ].map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(account.clinicName || 'wholesale').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-pricing.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    });

    importInput?.addEventListener('change', async () => {
      const file = importInput.files?.[0];
      if (!file) return;
      const imported = parseCsv(await file.text());
      let count = 0;
      imported.forEach((entry) => {
        const id = entry['variant id'];
        const price = Number(String(entry['account price'] || entry.price || '').replace(/[$,]/g, ''));
        if (id && Number.isFinite(price)) {
          editedPricing[id] = price;
          count += 1;
        }
      });
      importInput.value = '';
      renderRows();
      showMessage(`Imported ${count} variant prices. Review and save to apply them.`, count ? 'success' : 'error');
    });

    logoutButton?.addEventListener('click', () => {
      setToken('');
      accounts = [];
      applications = [];
      catalog = [];
      pricing = {};
      editedPricing = {};
      if (workspace) workspace.hidden = true;
      if (signin) signin.hidden = false;
      if (loginForm) loginForm.reset();
      if (rows) rows.innerHTML = '<tr><td colspan="7">Log in to load account pricing.</td></tr>';
      showMessage('Logged out of wholesale admin.', 'success');
    });

    if (token()) {
      loadWorkspace().catch((error) => {
        setToken('');
        if (workspace) workspace.hidden = true;
        if (signin) signin.hidden = false;
        showMessage(error.message, 'error');
      });
    }
  };

  const boot = () => document.querySelectorAll('[data-vl-wholesale-admin]').forEach(initAdmin);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
